<template>
  <div class="body bank-app">
    <Navbar :show-back-button="true" @back="navigateToDashboard"/>
    
    <div class="section-header">
      <!-- <button class="back-arrow" @click="navigateToDashboard">
        →
      </button> -->
      <h3 class="section-title mt-3" style="margin-right: 10px;">{{ $t('employer.TaseerSood.title_main') }}</h3>
    </div>
    
    <!-- Report Controls -->
    <div class="action-bar">
      <div style="display: inline-flex; gap: 2px;">
        <button 
          class="refresh-btn" 
          @click="loadReportData"
          :disabled="loading"
          :title="$t('employer.TaseerSood.btn_reload')"
        >
          🔄
          <span class="refresh-text">{{ $t('employer.TaseerSood.btn_reload') }}</span>
        </button>
  
        <button class="print-btn" @click="printReport">
          🖨️ 
          <span class="print-text">{{ $t('employer.TaseerSood.btn_print') }}</span>
        </button>
  
        <button class="export-btn" @click="exportToExcel">
          📊
          <span class="export-text">{{ $t('employer.TaseerSood.btn_export_excel') }}</span>
        </button>
      </div>

      <!-- Report Type Selector -->
      <div class="currency-selector">
        <label for="report-type-select" class="currency-label">
          {{ $t('employer.TaseerSood.label_report_type') }}:
        </label>
        <select 
          id="report-type-select"
          v-model="selectedReportType" 
          @change="loadReportData"
          class="currency-select"
          :disabled="loading"
        >
          <option value="0">{{ $t('employer.TaseerSood.option_all') }}</option>
          <option value="1">{{ $t('employer.TaseerSood.option_debitors') }}</option>
          <option value="2">{{ $t('employer.TaseerSood.option_creditors') }}</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>{{ $t('employer.TaseerSood.loading_main') }}</span>
      <div class="loading-details">
        <p>💱 {{ $t('employer.TaseerSood.loading_rates') }}</p>
        <p>📊 {{ $t('employer.TaseerSood.loading_calculations') }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h4>{{ $t('employer.TaseerSood.error_title') }}</h4>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadReportData">
        {{ $t('employer.TaseerSood.btn_retry') }}
      </button>
    </div>

    <!-- Report Content -->
    <div v-if="!loading && !error" class="report-content">
      
      <!-- Report Header -->
      <div class="report-header">
        <h4>{{ $t('employer.TaseerSood.header_currency_list') }}</h4>
        <div class="report-info">
          <span>{{ $t('employer.TaseerSood.label_currency_count') }}: {{ filteredCurrencies.length }}</span>
          <span> | </span>
          <span>{{ $t('employer.TaseerSood.label_total_balance') }}: {{ formatCurrency(totalForeignBalance) }}</span>
          <span> | </span>
          <span>{{ $t('employer.TaseerSood.label_total_profit_loss') }}: {{ formatCurrency(totalProfitLoss) }}</span>
        </div>
        
        <!-- Sorting Controls -->
        <div class="sort-controls">
          <select v-model="sortField" @change="sortOrder = 'desc'" class="sort-select">
            <option value="profitLoss">{{ $t('employer.TaseerSood.sort_profit_loss') }}</option>
            <option value="Arz">{{ $t('employer.TaseerSood.sort_currency_name') }}</option>
            <option value="MandeBedA">{{ $t('employer.TaseerSood.sort_foreign_balance') }}</option>
            <option value="MandeBedR">{{ $t('employer.TaseerSood.sort_rial_balance') }}</option>
            <option value="CurRate">{{ $t('employer.TaseerSood.sort_today_rate') }}</option>
            <option value="AveRate">{{ $t('employer.TaseerSood.sort_average_rate') }}</option>
          </select>

          <button @click="sortOrder = sortOrder === 'asc' ? 'desc' : 'asc'" class="sort-order-btn">
            {{ sortOrder === 'asc' ? $t('employer.TaseerSood.sort_asc') + ' ↑' : $t('employer.TaseerSood.sort_desc') + ' ↓' }}
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredCurrencies.length === 0" class="empty-state">
        <div class="empty-icon">💱</div>
        <h4>{{ $t('employer.TaseerSood.label_no_change_currencies') }}</h4>
        <p>برای ارز انتخابی اطلاعات موجود نیست.</p>
        <button class="retry-btn" @click="loadReportData">
          {{ $t('employer.TaseerSood.btn_reload') }}
        </button>
      </div>

      <!-- Currency Cards -->
      <div v-else class="currency-cards-container">
        <div class="currency-cards-grid">
          
          <div 
            v-for="currency in paginatedCurrencies" 
            :key="currency.Arz" 
            class="currency-card"
          >
            <div class="card-body">

              <!-- Currency Header -->
              <div class="currency-header">
                <div class="currency-title">
                  <span class="currency-symbol">{{ getCurrencySymbol(currency.Arz) }}</span>
                  <span class="currency-name">{{ currency.Arz }}</span>
                  <div class="currency-status" :class="getCurrencyProfitLossClass(currency)">
                    {{ getProfitLossText(currency) }}
                  </div>
                </div>
              </div>

              <!-- Grid -->
              <div class="currency-details-grid">

                <div class="grid-row">
                  <div class="grid-item">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_balance_foreign') }}:</div>
                    <div class="grid-value foreign-amount">
                      {{ formatCurrency(currency.MandeBedA) }}
                    </div>
                  </div>

                  <div class="grid-item">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_balance_rial') }}:</div>
                    <div class="grid-value rial-amount">
                      {{ formatCurrency(currency.MandeBedR) }}
                    </div>
                  </div>
                </div>

                <div class="grid-row">
                  <div class="grid-item">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_average_rate') }}:</div>
                    <div class="grid-value average-rate">
                      {{ formatCurrency(currency.AveRate) }}
                    </div>
                  </div>

                  <div class="grid-item highlighted">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_today_rate') }}:</div>
                    <div class="grid-value current-rate">
                      {{ formatCurrency(currency.CurRate) }}
                    </div>
                  </div>
                </div>

                <div class="grid-row">
                  <div class="grid-item">
                    <div class="grid-label">ارزش امروز:</div>
                    <div class="grid-value today-value">
                      {{ formatCurrency(currency.CurMandeBed) }}
                    </div>
                  </div>

                  <div v-if="currency.MandeBedR > 0" class="grid-item percentage-row">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_percentage') }}:</div>
                    <div class="grid-value percentage-value" :class="getPercentageClass(currency)">
                      {{ getPercentageChange(currency) }}%
                    </div>
                  </div>
                </div>

                <div class="grid-row-full">
                  <div class="grid-item-full profit-loss-row" :class="getProfitLossRowClass(currency)">
                    <div class="grid-label">{{ $t('employer.TaseerSood.label_profit_loss') }}:</div>
                    <div class="grid-content">
                      <span class="grid-value profit-loss-value">
                        {{ formatCurrency(Math.abs(currency.CurMandeBed - currency.MandeBedR)) }}
                      </span>
                      <span class="profit-loss-indicator">
                        {{ getProfitLossIndicator(currency) }}
                      </span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>

        <!-- Pagination -->
        <div v-if="filteredCurrencies.length > itemsPerPage" class="pagination-controls">
          <button 
            @click="prevPage" 
            :disabled="currentPage === 1"
            class="pagination-btn"
          >
            {{ $t('employer.TaseerSood.btn_prev') }}
          </button>

          <span class="page-info">
            {{ $t('employer.TaseerSood.label_page') }} {{ currentPage }}
            {{ $t('employer.TaseerSood.label_from') }} {{ totalPages }}
          </span>

          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages"
            class="pagination-btn"
          >
            {{ $t('employer.TaseerSood.btn_next') }}
          </button>
        </div>
      </div>

      <!-- Overall Summary -->
      <div v-if="filteredCurrencies.length > 0" class="overall-summary">
        <div class="summary-cards">

          <div class="summary-card total-balance-card">
            <div class="summary-header">
              <span class="summary-title">{{ $t('employer.TaseerSood.summary_total_foreign_balance') }}</span>
              <span class="summary-badge">💱</span>
            </div>
            <div class="summary-amount">
              {{ formatCurrency(totalForeignBalance) }}
            </div>
            <div class="summary-subtitle">
              {{ filteredCurrencies.length }} {{ $t('employer.TaseerSood.summary_currency_count') }}
            </div>
          </div>

          <div class="summary-card profit-card" v-if="totalProfit > 0">
            <div class="summary-header">
              <span class="summary-title">{{ $t('employer.TaseerSood.label_profit_currencies') }}</span>
              <span class="summary-badge">📈</span>
            </div>
            <div class="summary-amount profit-amount">
              +{{ formatCurrency(totalProfit) }}
            </div>
            <div class="summary-subtitle">
              {{ profitableCurrenciesCount }} ارز
            </div>
          </div>

          <div class="summary-card loss-card" v-if="totalLoss > 0">
            <div class="summary-header">
              <span class="summary-title">{{ $t('employer.TaseerSood.label_loss_currencies') }}</span>
              <span class="summary-badge">📉</span>
            </div>
            <div class="summary-amount loss-amount">
              -{{ formatCurrency(totalLoss) }}
            </div>
            <div class="summary-subtitle">
              {{ lossCurrenciesCount }} ارز
            </div>
          </div>

          <div class="summary-card net-card" :class="getNetCardClass">
            <div class="summary-header">
              <span class="summary-title">{{ $t('employer.TaseerSood.summary_total_profit_loss') }}</span>
              <span class="summary-badge">⚖️</span>
            </div>
            <div class="summary-amount" :class="getNetAmountClass">
              {{ formatCurrency(totalProfitLoss) }}
            </div>
            <div class="summary-subtitle">
              {{ getNetStatusText }}
            </div>
          </div>

        </div>

        <!-- Distribution -->
        <div class="currency-distribution">
          <h5>{{ $t('employer.TaseerSood.title_distribution') }}</h5>

          <div class="distribution-list">
            <div 
              v-for="currency in currencyDistribution" 
              :key="currency.Arz"
              class="distribution-item"
            >
              <div class="distribution-header">
                <span class="currency-name-small">{{ currency.Arz }}</span>
                <span class="percentage">{{ currency.percentage }}%</span>
              </div>

              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: currency.percentage + '%' }"
                  :class="getProgressBarClass(currency)"
                ></div>
              </div>

              <div class="distribution-details">
                <span class="amount">{{ formatCurrency(currency.MandeBedA) }}</span>
                <span class="profit-loss" :class="getDistributionProfitLossClass(currency)">
                  {{ getDistributionProfitLossText(currency) }}
                </span>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'

export default {
  name: 'TaseerSoodReport',
  components: {
    Navbar
  },
  setup() {
    const router = useRouter()
    
    // Data states
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    
    // Report data
    const reportData = ref({
      data: [],
      total_records: 0,
    })
    
    // Report type
    const selectedReportType = ref(0) // 0 = all, 1 = bed, 2 = bes
    
    // Pagination states
    const itemsPerPage = 10
    const currentPage = ref(1)
    
    // Sorting states
    const sortField = ref('profitLoss')
    const sortOrder = ref('desc')
    
    // Load report data
    const loadReportData = async () => {
      loading.value = true
      error.value = null
      
      try {
        console.log(`📊 Loading taseer sood report with type: ${selectedReportType.value}`)
        
        const request = {
          bedorBes: parseInt(selectedReportType.value) || 3
        }
        
        const response = await employerService.getTaseerSood(request)
        
        if (response.data.success) {
          reportData.value = response.data
          console.log(`✅ Taseer Sood report loaded: ${response.data.total_records} currency records`)
          
          // Reset to first page
          currentPage.value = 1
        } else {
          throw new Error(response.data.message || 'خطا در بارگذاری گزارش')
        }
      } catch (err) {
        console.error('❌ Error loading taseer sood report:', err)
        error.value = err.response?.data?.detail || err.message || 'خطا در بارگذاری گزارش'
      } finally {
        loading.value = false
      }
    }
    
    // Filter currencies (for future search functionality)
    const filteredCurrencies = computed(() => {
      let currencies = reportData.value.data || []
      
      // Apply search if implemented
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        currencies = currencies.filter(currency => 
          currency.Arz?.toLowerCase().includes(query)
        )
      }
      
      // Apply sorting
      return sortData(currencies)
    })
    
    // Sorting function
    const sortData = (data) => {
      return [...data].sort((a, b) => {
        let aValue, bValue
        
        switch (sortField.value) {
          case 'Arz':
            aValue = a.Arz || ''
            bValue = b.Arz || ''
            break
          case 'MandeBedA':
            aValue = a.MandeBedA || 0
            bValue = b.MandeBedA || 0
            break
          case 'MandeBedR':
            aValue = a.MandeBedR || 0
            bValue = b.MandeBedR || 0
            break
          case 'CurRate':
            aValue = a.CurRate || 0
            bValue = b.CurRate || 0
            break
          case 'AveRate':
            aValue = a.AveRate || 0
            bValue = b.AveRate || 0
            break
          case 'profitLoss':
          default:
            aValue = (a.CurMandeBed || 0) - (a.MandeBedR || 0)
            bValue = (b.CurMandeBed || 0) - (b.MandeBedR || 0)
        }
        
        if (sortOrder.value === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    }
    
    // Pagination
    const paginatedCurrencies = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage
      return filteredCurrencies.value.slice(start, start + itemsPerPage)
    })
    
    const totalPages = computed(() => {
      return Math.ceil(filteredCurrencies.value.length / itemsPerPage)
    })
    
    const prevPage = () => currentPage.value > 1 && currentPage.value--
    const nextPage = () => currentPage.value < totalPages.value && currentPage.value++
    
    // Search handler
    const onSearch = () => {
      currentPage.value = 1
    }
    
    // Helper functions for calculations
    const getProfitLoss = (currency) => {
      return (currency.CurMandeBed || 0) - (currency.MandeBedR || 0)
    }
    
    const getPercentageChange = (currency) => {
      if (!currency.MandeBedR || currency.MandeBedR === 0) return 0
      const profitLoss = getProfitLoss(currency)
      return ((profitLoss / currency.MandeBedR) * 100).toFixed(2)
    }
    
    const getProfitLossText = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'سود'
      if (profitLoss < 0) return 'زیان'
      return 'بدون تغییر'
    }
    
    const getProfitLossIndicator = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return '↑ سود'
      if (profitLoss < 0) return '↓ زیان'
      return '= بدون تغییر'
    }
    
    const getCurrencyProfitLossClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'status-profit'
      if (profitLoss < 0) return 'status-loss'
      return 'status-neutral'
    }
    
    const getProfitLossRowClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'profit-row'
      if (profitLoss < 0) return 'loss-row'
      return 'neutral-row'
    }
    
    const getPercentageClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'percentage-profit'
      if (profitLoss < 0) return 'percentage-loss'
      return 'percentage-neutral'
    }
    
    const getSummaryLabel = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'سود تسعیر'
      if (profitLoss < 0) return 'زیان تسعیر'
      return 'بدون تغییر'
    }
    
    const getSummaryDetail = (currency) => {
      const profitLoss = getProfitLoss(currency)
      const percentage = getPercentageChange(currency)
      if (profitLoss === 0) return ''
      return `${percentage}% ${profitLoss > 0 ? 'افزایش' : 'کاهش'}`
    }
    
    const getSummaryBoxClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'summary-profit'
      if (profitLoss < 0) return 'summary-loss'
      return 'summary-neutral'
    }
    
    // Totals
    const totalForeignBalance = computed(() => {
      return filteredCurrencies.value.reduce((total, currency) => 
        total + (currency.MandeBedA || 0), 0
      )
    })
    
    const totalProfit = computed(() => {
      return filteredCurrencies.value.reduce((total, currency) => {
        const profitLoss = getProfitLoss(currency)
        return profitLoss > 0 ? total + profitLoss : total
      }, 0)
    })
    
    const totalLoss = computed(() => {
      return filteredCurrencies.value.reduce((total, currency) => {
        const profitLoss = getProfitLoss(currency)
        return profitLoss < 0 ? total + Math.abs(profitLoss) : total
      }, 0)
    })
    
    const totalProfitLoss = computed(() => {
      return totalProfit.value - totalLoss.value
    })
    
    const profitableCurrenciesCount = computed(() => {
      return filteredCurrencies.value.filter(currency => getProfitLoss(currency) > 0).length
    })
    
    const lossCurrenciesCount = computed(() => {
      return filteredCurrencies.value.filter(currency => getProfitLoss(currency) < 0).length
    })
    
    const getTotalProfitLossClass = computed(() => {
      if (totalProfitLoss.value > 0) return 'profit-amount'
      if (totalProfitLoss.value < 0) return 'loss-amount'
      return ''
    })
    
    const getNetCardClass = computed(() => {
      if (totalProfitLoss.value > 0) return 'net-profit-card'
      if (totalProfitLoss.value < 0) return 'net-loss-card'
      return 'net-neutral-card'
    })
    
    const getNetAmountClass = computed(() => {
      if (totalProfitLoss.value > 0) return 'net-profit-amount'
      if (totalProfitLoss.value < 0) return 'net-loss-amount'
      return 'net-neutral-amount'
    })
    
    const getNetStatusText = computed(() => {
      if (totalProfitLoss.value > 0) return `سود خالص (${profitableCurrenciesCount.value} ارز مثبت)`
      if (totalProfitLoss.value < 0) return `زیان خالص (${lossCurrenciesCount.value} ارز منفی)`
      return 'تعادل کامل'
    })
    
    // Currency distribution for chart
    const currencyDistribution = computed(() => {
      const currencies = filteredCurrencies.value
      const total = totalForeignBalance.value
      
      if (total === 0) return []
      
      return currencies.map(currency => {
        const percentage = ((currency.MandeBedA / total) * 100).toFixed(1)
        return {
          ...currency,
          percentage: parseFloat(percentage)
        }
      }).sort((a, b) => b.percentage - a.percentage)
    })
    
    const getProgressBarClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'progress-profit'
      if (profitLoss < 0) return 'progress-loss'
      return 'progress-neutral'
    }
    
    const getDistributionProfitLossClass = (currency) => {
      const profitLoss = getProfitLoss(currency)
      if (profitLoss > 0) return 'distribution-profit'
      if (profitLoss < 0) return 'distribution-loss'
      return 'distribution-neutral'
    }
    
    const getDistributionProfitLossText = (currency) => {
      const profitLoss = getProfitLoss(currency)
      const percentage = getPercentageChange(currency)
      if (profitLoss === 0) return 'بدون تغییر'
      const sign = profitLoss > 0 ? '+' : ''
      return `${sign}${percentage}%`
    }
    
    // Currency symbols
    const getCurrencySymbol = (currencyName) => {
      const symbols = {
        'ريال': '﷼',
        'ریال': '﷼',
        'دلار': '$',
        'یورو': '€',
        'درهم': 'د.إ',
        'پوند': '£',
        'یوان': '¥',
        'لیر': '₺',
        'ین': '¥',
        'روپیه': '₹',
        'وون': '₩',
        'روبل': '₽',
        'کرون': 'kr',
        'فرانک': 'Fr',
        'دینار': 'د.ك',
        'درهم امارات': 'د.إ',
        'ریال سعودی': 'ر.س',
        'دینار کویت': 'د.ك',
      }
      return symbols[currencyName] || currencyName
    }
    
    // Navigation
    const navigateToDashboard = () => {
      router.push('/employer/dashboard')
    }
    
    // Export functions
    const printReport = () => {
      console.log('Printing taseer sood report...')
      window.print()
    }
    
    const exportToExcel = () => {
      console.log('Exporting taseer sood report to Excel...')
      // Implement Excel export here
      alert('این قابلیت به زودی اضافه خواهد شد.')
    }
    
    // Format functions
    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(Math.round(amount))
    }
    
    // Watch for report type changes
    watch(selectedReportType, () => {
      loadReportData()
    })
    
    // Initialize
    onMounted(async () => {
      await loadReportData()
    })
    
    return {
      // Data
      loading,
      error,
      searchQuery,
      reportData,
      
      // UI states
      selectedReportType,
      
      // Pagination
      currentPage,
      paginatedCurrencies,
      totalPages,
      itemsPerPage,
      
      // Sorting
      sortField,
      sortOrder,
      
      // Computed
      filteredCurrencies,
      totalForeignBalance,
      totalProfit,
      totalLoss,
      totalProfitLoss,
      profitableCurrenciesCount,
      lossCurrenciesCount,
      currencyDistribution,
      getTotalProfitLossClass,
      getNetCardClass,
      getNetAmountClass,
      getNetStatusText,
      
      // Methods
      loadReportData,
      onSearch,
      prevPage,
      nextPage,
      printReport,
      exportToExcel,
      navigateToDashboard,
      formatCurrency,
      getCurrencySymbol,
      getProfitLoss,
      getPercentageChange,
      getProfitLossText,
      getProfitLossIndicator,
      getCurrencyProfitLossClass,
      getProfitLossRowClass,
      getPercentageClass,
      getSummaryLabel,
      getSummaryDetail,
      getSummaryBoxClass,
      getProgressBarClass,
      getDistributionProfitLossClass,
      getDistributionProfitLossText,
    }
  }
}
</script>

<style scoped>
/* Main container */
.bank-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  direction: rtl;
  padding: 10px;
  background: var(--bg);
  color: var(--text);
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.4rem;
  color: var(--heading);
  margin: 0;
  font-weight: 600;
}

.section-subtitle {
  font-size: 0.9rem;
  color: var(--subtext);
  margin: 0;
  margin-right: auto;
  padding-right: 10px;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 5px 15px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  color: var(--primary);
  margin-left: 15px;
}

.back-arrow:hover {
  background-color: var(--hover);
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-1);
  flex-wrap: wrap;
  border: 1px solid var(--card-border);
}

.refresh-btn, .print-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.refresh-btn {
  background: var(--primary);
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--primary-hover);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.print-btn {
  background: var(--header-1);
  color: white;
}

.print-btn:hover {
  background: var(--header-2);
}

.export-btn {
  background: var(--green);
  color: white;
}

.export-btn:hover {
  background: var(--orange-2);
}

/* Currency Selector */
.currency-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.currency-label {
  font-weight: 500;
  color: var(--subtext);
}

.currency-select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text);
  font-size: 14px;
  min-width: 150px;
}

/* Summary Info */
.summary-info {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 15px;
  background: var(--bg);
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.info-label {
  font-size: 13px;
  color: var(--subtext);
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.info-divider {
  color: var(--muted-2);
}

.profit-amount {
  color: var(--green);
}

.loss-amount {
  color: var(--orange-1);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--subtext);
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-1);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--card-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-details {
  margin-top: 15px;
  text-align: center;
  color: var(--subtext);
}

.loading-details p {
  margin: 5px 0;
  font-size: 14px;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--orange-1);
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-1);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.error-state h4 {
  margin: 0 0 10px 0;
  color: var(--heading);
}

.error-state p {
  color: var(--subtext);
  margin-bottom: 20px;
}

.retry-btn {
  padding: 10px 25px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background: var(--primary-hover);
}

/* Report Content */
.report-content {
  padding: 10px 0;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--card-border);
  flex-wrap: wrap;
  gap: 15px;
}

.report-header h4 {
  margin: 0;
  color: var(--heading);
  font-size: 18px;
}

.report-info {
  color: var(--subtext);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  padding: 8px 15px;
  border-radius: 8px;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text);
  font-size: 14px;
  min-width: 140px;
  direction: rtl;
}

.sort-order-btn {
  padding: 8px 15px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.sort-order-btn:hover {
  background: var(--primary-hover);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--subtext);
  font-size: 16px;
  background: var(--card-bg);
  border-radius: 12px;
  border: 2px dashed var(--card-border);
  margin: 20px 0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

/* Currency Cards Container */
.currency-cards-container {
  padding: 10px 0;
}

.currency-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

/* Currency Card */
.currency-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px var(--shadow-1);
  border: 1px solid var(--card-border);
  transition: all 0.3s ease;
  overflow: hidden;
  height: 100%;
}

.currency-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px var(--shadow-2);
  border-color: var(--primary);
}

/* Card Body */
.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Currency Header */
.currency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--card-border);
}

.currency-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.currency-symbol {
  font-size: 24px;
  font-weight: bold;
  color: var(--heading);
}

.currency-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--heading);
}

.currency-status {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.status-profit {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
  color: var(--green);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-loss {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.2) 100%);
  color: var(--orange-1);
  border: 1px solid rgba(249, 115, 22, 0.2);
}

.status-neutral {
  background: var(--bg);
  color: var(--muted);
  border: 1px solid var(--card-border);
}

/* Currency Details */
.currency-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--card-border);
}

.detail-row.highlighted {
  background: var(--ring);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--primary);
}

.detail-label {
  font-size: 14px;
  color: var(--subtext);
  font-weight: 500;
}

.detail-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

.foreign-amount {
  color: var(--primary);
}

.rial-amount {
  color: var(--text);
}

.average-rate {
  color: var(--muted);
}

.current-rate {
  color: var(--orange-1);
  font-weight: 700;
}

.today-value {
  color: var(--green);
  font-weight: 700;
}

.profit-loss-row {
  border-bottom: none;
  padding: 12px;
  border-radius: 8px;
  margin-top: 5px;
}

.profit-row {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.loss-row {
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid rgba(249, 115, 22, 0.3);
}

.neutral-row {
  background: var(--bg);
  border: 1px solid var(--card-border);
}

.profit-loss-value {
  font-size: 16px;
  font-weight: 700;
}

.profit-loss-indicator {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.profit-row .profit-loss-value {
  color: var(--green);
}

.profit-row .profit-loss-indicator {
  background: var(--green);
  color: white;
}

.loss-row .profit-loss-value {
  color: var(--orange-1);
}

.loss-row .profit-loss-indicator {
  background: var(--orange-1);
  color: white;
}

.neutral-row .profit-loss-value {
  color: var(--muted);
}

.neutral-row .profit-loss-indicator {
  background: var(--muted-2);
  color: white;
}

.percentage-row {
  border-bottom: none;
}

.percentage-value {
  font-size: 14px;
  font-weight: 600;
}

.percentage-profit {
  color: var(--green);
}

.percentage-loss {
  color: var(--orange-1);
}

.percentage-neutral {
  color: var(--muted);
}

/* Currency Summary */
.currency-summary {
  margin-top: 10px;
}

.summary-box {
  padding: 15px;
  border-radius: 10px;
  text-align: center;
}

.summary-profit {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
  border: 1px solid var(--green);
}

.summary-loss {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.2) 100%);
  border: 1px solid var(--orange-1);
}

.summary-neutral {
  background: var(--bg);
  border: 1px solid var(--card-border);
}

.summary-label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
}

.summary-profit .summary-label {
  color: var(--green);
}

.summary-loss .summary-label {
  color: var(--orange-1);
}

.summary-neutral .summary-label {
  color: var(--heading);
}

.summary-amount {
  display: block;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 5px;
}

.summary-profit .summary-amount {
  color: var(--green);
}

.summary-loss .summary-amount {
  color: var(--orange-1);
}

.summary-neutral .summary-amount {
  color: var(--heading);
}

.summary-detail {
  display: block;
  font-size: 13px;
  color: var(--muted);
}

/* Overall Summary */
.overall-summary {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 2px solid var(--card-border);
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.summary-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px var(--shadow-1);
  border-top: 4px solid;
}

.total-balance-card {
  border-top-color: var(--primary);
}

.profit-card {
  border-top-color: var(--green);
}

.loss-card {
  border-top-color: var(--orange-1);
}

.net-profit-card {
  border-top-color: var(--green);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
}

.net-loss-card {
  border-top-color: var(--orange-1);
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(249, 115, 22, 0.1) 100%);
}

.net-neutral-card {
  border-top-color: var(--muted);
  background: var(--bg);
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--heading);
}

.summary-badge {
  font-size: 20px;
}

.summary-card .summary-amount {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
}

.total-balance-card .summary-amount {
  color: var(--primary);
}

.profit-amount {
  color: var(--green);
}

.loss-amount {
  color: var(--orange-1);
}

.net-profit-amount {
  color: var(--green);
}

.net-loss-amount {
  color: var(--orange-1);
}

.net-neutral-amount {
  color: var(--muted);
}

.summary-subtitle {
  font-size: 14px;
  color: var(--subtext);
}

/* Currency Distribution */
.currency-distribution {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  padding-bottom: 65px;

  box-shadow: 0 2px 8px var(--shadow-1);
}

.currency-distribution h5 {
  margin: 0 0 15px 0;
  color: var(--heading);
  font-size: 16px;
  font-weight: 600;
}

.distribution-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.distribution-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.distribution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.currency-name-small {
  font-size: 14px;
  font-weight: 500;
  color: var(--heading);
}

.percentage {
  font-size: 13px;
  font-weight: 600;
  color: var(--subtext);
}

.progress-bar {
  height: 8px;
  background: var(--card-border);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-profit {
  background: linear-gradient(90deg, var(--green) 0%, var(--green) 100%);
}

.progress-loss {
  background: linear-gradient(90deg, var(--orange-1) 0%, var(--orange-2) 100%);
}

.progress-neutral {
  background: linear-gradient(90deg, var(--muted-2) 0%, var(--muted) 100%);
}

.distribution-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.amount {
  color: var(--subtext);
}

.distribution-profit {
  color: var(--green);
  font-weight: 600;
}

.distribution-loss {
  color: var(--orange-1);
  font-weight: 600;
}

.distribution-neutral {
  color: var(--muted);
}

/* Currency Details Grid */
.currency-details-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
}

.grid-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.grid-row-full {
  margin-top: 5px;
}

.grid-item {
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: var(--bg);
  border-radius: 8px;
  border: 1px solid var(--card-border);
  transition: all 0.2s ease;
}

.grid-item.highlighted {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.15) 100%);
  border: 1px solid var(--orange-1);
  box-shadow: 0 2px 4px var(--shadow-1);
}

.grid-item.percentage-row {
  padding: 10px;
}

.grid-label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 500;
  margin-bottom: 4px;
  text-align: center;
}

.grid-value {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: var(--text);
}

.foreign-amount {
  color: var(--primary);
}

.rial-amount {
  color: var(--text);
}

.average-rate {
  color: var(--muted);
}

.current-rate {
  color: var(--orange-1);
  font-weight: 700;
}

.today-value {
  color: var(--green);
  font-weight: 700;
}

/* Full width profit/loss row */
.grid-item-full {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-radius: 8px;
  margin-top: 5px;
}

.profit-loss-row {
  border: 1px solid transparent;
}

.profit-row {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid var(--green);
}

.loss-row {
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid var(--orange-1);
}

.neutral-row {
  background: var(--bg);
  border: 1px solid var(--card-border);
}

.grid-item-full .grid-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0;
}

.grid-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.profit-loss-value {
  font-size: 16px;
  font-weight: 700;
}

.profit-row .profit-loss-value {
  color: var(--green);
}

.loss-row .profit-loss-value {
  color: var(--orange-1);
}

.neutral-row .profit-loss-value {
  color: var(--heading);
}

.profit-loss-indicator {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.profit-row .profit-loss-indicator {
  background: var(--green);
  color: white;
}

.loss-row .profit-loss-indicator {
  background: var(--orange-1);
  color: white;
}

.neutral-row .profit-loss-indicator {
  background: var(--muted);
  color: white;
}

.percentage-value {
  font-size: 14px;
  font-weight: 600;
}

.percentage-profit {
  color: var(--green);
}

.percentage-loss {
  color: var(--orange-1);
}

.percentage-neutral {
  color: var(--muted);
}

/* Summary Section Grid */
.currency-summary-grid {
  margin-top: 5px;
}

.summary-box {
  padding: 15px;
  border-radius: 10px;
  text-align: center;
  width: 100%;
}

.summary-profit {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
  border: 1px solid var(--green);
}

.summary-loss {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.2) 100%);
  border: 1px solid var(--orange-1);
}

.summary-neutral {
  background: var(--bg);
  border: 1px solid var(--card-border);
}

.summary-label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
}

.summary-profit .summary-label {
  color: var(--green);
}

.summary-loss .summary-label {
  color: var(--orange-1);
}

.summary-neutral .summary-label {
  color: var(--heading);
}

.summary-amount {
  display: block;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 5px;
}

.summary-profit .summary-amount {
  color: var(--green);
}

.summary-loss .summary-amount {
  color: var(--orange-1);
}

.summary-neutral .summary-amount {
  color: var(--heading);
}

.summary-detail {
  display: block;
  font-size: 13px;
  color: var(--muted);
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .currency-selector {
    width: 100%;
    justify-content: space-between;
  }
  
  .summary-info {
    width: 100%;
    justify-content: space-around;
  }
  
  .currency-cards-grid {
    grid-template-columns: 1fr;
  }
  
  .report-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .sort-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .sort-select {
    flex-grow: 1;
  }
  
  .summary-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .currency-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .currency-status {
    align-self: flex-start;
  }
  
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .profit-loss-row {
    flex-direction: row;
    align-items: center;
  }
  
  .grid-label {
    font-size: 11px;
  }
  
  .grid-value {
    font-size: 13px;
  }
  
  .profit-loss-value {
    font-size: 14px;
  }
  
  .summary-label {
    font-size: 14px;
  }
  
  .summary-amount {
    font-size: 18px;
  }
}

@media print {
  .action-bar,
  .summary-cards,
  .sort-controls,
  .pagination-controls {
    display: none !important;
  }
  
  .bank-app {
    padding: 0;
    background: white;
  }
  
  .currency-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>